import { memo, useEffect } from "react";
import { Box, Grid2, Paper, Skeleton, SxProps, Typography } from "@mui/material";
import { useDocumentSelectionSlice } from "@store/slices/documentSelection";
import { useQuery } from "@apollo/client";
import { Query } from "@/app/graphql/codegen";
import { FETCH_DOCUMENT } from "@documents/graphql/queries/fetchDocument";
import PDFViewer from "../PDFViewer";
import { icon } from "@/app/style/icons";
import { useDebugBorder } from "@core/hooks/useDebugBorder";
import { availableViewportHeight } from "@core/layout/AppLayout";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setListSelection } from "@store/slices/documentSelection";

interface EmptyStateMessageProps {
    title: string;
    message?: string;
    Icon?: React.ElementType;
    sx?: SxProps;
}

const EmptyStateMessage = memo(({ title, message, Icon = icon.book, sx }: EmptyStateMessageProps) => {
    return (
        <Paper
            elevation={0}
            sx={{
                height: "740px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: 2,
                p: 3,
                ...sx,
            }}
        >
            <Box
                sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: "50%",
                    bgcolor: "background.paper",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Icon sx={{ fontSize: 60, opacity: 0.6 }} />
            </Box>
            <Typography variant="h5" gutterBottom align="center">
                {title}
            </Typography>
            {message && (
                <Typography variant="body1" color="text.secondary" align="center">
                    {message}
                </Typography>
            )}
        </Paper>
    );
});

/**
 * Displays the details of a selected document from the lexicon
 */
const LexiconDetails = memo(() => {
    const dispatch = useDispatch();
    const { listId } = useDocumentSelectionSlice();
    const [searchParams] = useSearchParams();
    
    // Support multiple parameter names for backward compatibility
    const documentId = searchParams.get("documentId") || searchParams.get("document_id");
    
    // For debugging
    useEffect(() => {
        if (documentId) {
            console.log("LexiconDetails: URL document ID:", documentId);
        }
        console.log("LexiconDetails: Current list ID:", listId);
    }, [documentId, listId]);
    
    // Ensure the document is selected in Redux when coming from a direct link
    useEffect(() => {
        if (documentId && documentId !== listId) {
            console.log("LexiconDetails: Setting document selection from URL:", documentId);
            dispatch(setListSelection(documentId));
        }
    }, [documentId, listId, dispatch]);

    // Get the effective ID to use (either from Redux or URL)
    const effectiveDocId = listId || documentId;

    // For debugging
    useEffect(() => {
        console.log("LexiconDetails: Effective doc ID:", effectiveDocId);
    }, [effectiveDocId]);

    const { data, loading } = useQuery<Pick<Query, "officialDocument">>(FETCH_DOCUMENT, {
        variables: { id: effectiveDocId },
        skip: !effectiveDocId
    });

    const doc = data?.officialDocument;
    const skeletonHeight = 32;

    const pdfHeight = `calc(${availableViewportHeight} - 138px)`;

    const border = useDebugBorder("orange");

    if ((effectiveDocId) && loading) {
        // Dokument lädt
        return (
            <Grid2 container size={12} spacing={2}>
                <Grid2 container size={12} spacing={1}>
                    <Grid2 size={"grow"}>
                        <Skeleton width={200} height={skeletonHeight} />
                    </Grid2>

                    <Grid2>
                        <Skeleton width={skeletonHeight} height={skeletonHeight} variant={"circular"} />
                    </Grid2>
                    <Grid2>
                        <Skeleton width={skeletonHeight} height={skeletonHeight} variant={"circular"} />
                    </Grid2>
                    <Grid2>
                        <Skeleton width={skeletonHeight} height={skeletonHeight} variant={"circular"} />
                    </Grid2>
                </Grid2>

                <Grid2 size={12}>
                    <Skeleton height={240} />
                </Grid2>

                <Grid2 container size={12}>
                    <Grid2>
                        <Skeleton width={82} height={skeletonHeight} />
                    </Grid2>
                    <Grid2>
                        <Skeleton width={96} height={skeletonHeight} />
                    </Grid2>
                    <Grid2>
                        <Skeleton width={69} height={skeletonHeight} />
                    </Grid2>
                </Grid2>
            </Grid2>
        );
    }

    if (!effectiveDocId) {
        // Kein Dokument ausgewählt
        return <EmptyStateMessage title="Kein Dokument ausgewählt" message="Bitte wählen Sie ein Dokument aus der Liste auf der linken Seite aus." Icon={icon.book} />;
    }

    if (!doc) {
        // Dokument nicht gefunden
        return <EmptyStateMessage title="Dokument nicht gefunden" message="Das ausgewählte Dokument wurde nicht gefunden oder ist nicht mehr verfügbar." Icon={icon.cancel} />;
    }

    // Document found and loaded
    return (
        <Grid2 container size={12} spacing={2} justifyContent={"end"}>
            <Grid2 size={12}>
                <Typography variant="h5">{doc.name || "Unnamed Chat"}</Typography>
            </Grid2>

            <Grid2 size={12} border={border} height={pdfHeight}>
                <PDFViewer url={doc.pdfFileUrl} />
            </Grid2>
        </Grid2>
    );
});

export default LexiconDetails;
